import {ApiProvider, ch} from "@renta-apps/athenaeum-react-common";
import {ConstructionSiteInfo} from "@/models/server/ConstructionSiteInfo";
import {ConstructionSiteDetailsResponse} from "@/models/server/Responses/ConstructionSiteDetailsResponse";
import queryString from "query-string";
import {ConstructionSiteName} from "@/models/server/ConstructionSiteName";
import {EmissionFilterTimeFrame, ServiceType} from "@/models/Enums";
import {IEquipmentPageData, IEquipmentRequest, IResponse} from "@/pages/ConstructionSiteDetails/Equipments/types";
import CreateConstructionSiteServiceRequestRequest from "@/models/server/Requests/CreateConstructionSiteServiceRequestRequest";
import GetConstructionSiteDiscountRequest from "@/models/server/Requests/GetConstructionSiteDiscountRequest";
import ReturnModel from "@/models/server/ReturnModel";
import InviteUserPostRequest from "@/models/server/InviteUserPostRequest";
import LogDocumentEvent from "@/models/server/LogDocumentEvent";
import OrganizationContractDiscounts from "@/models/server/OrganizationContractDiscounts";
import ResolveLocationRequest from "@/models/server/Requests/ResolveLocationRequest";
import LocationModel from "@/models/server/LocationModel";
import ConstructionSiteUserPagedListRequest from "@/models/server/ConstructionSiteUserPagedListRequest";
import {FileModel, GeoLocation, IPagedList, SortDirection} from "@renta-apps/athenaeum-toolkit";
import ConstructionSiteUserModel from "@/models/server/ConstructionSiteUserModel";
import SitePowerInfoModel from "@/models/server/SitePowerInfoModel";
import BasicChartModel from "@/models/server/BasicChartModel";
import SaveSiteLocationRequest from "@/models/server/Requests/SaveSiteLocationRequest";
import GetEquipmentEmissionData from "@/models/server/Requests/GetEquipmentEmissionData";
import ProductsWithTelematicsRequest from "@/models/server/Requests/ProductsWithTelematicsRequest";
import {ProductWithTelematics} from "@/models/server/Models/Equipment/ProductWithTelematics";
import {ProductWithTelematicsDetails} from "@/models/server/Models/Equipment/ProductWithTelematicsDetails";
import ProductWithTelematicsDetailsRequest from "@/models/server/Requests/ProductWithTelematicsDetailsRequest";
import EquipmentDataReportRequest from "@/models/server/Requests/EquipmentDataReportRequest";

export const getConstructionSiteDetails = async (constructionSiteId: string): Promise<ConstructionSiteDetailsResponse> => {
    return ApiProvider.postAsync<ConstructionSiteDetailsResponse>("/api/ConstructionSites/GetConstructionSiteDetails", constructionSiteId, null);
};

export const getConstructionSiteDiscounts = async (contractId: string, constructionSiteId: string, productExternalIds: string[]): Promise<OrganizationContractDiscounts> => {
    const request: GetConstructionSiteDiscountRequest = {
        contractId,
        constructionSiteId,
        productExternalIds
    }
    return ApiProvider.postAsync<OrganizationContractDiscounts>("/api/ConstructionSites/GetConstructionSiteDiscounts", request, null);
};

export const getConstructionSiteInfos = async (contractId: string): Promise<ConstructionSiteInfo[]> => {
    return ApiProvider.postAsync<ConstructionSiteInfo[]>("/api/ConstructionSites/GetConstructionSiteInfos", contractId, null);
};

export const searchConstructionSiteNames = async (searchTerm: string, contractId: string, searchLimit: number = 50): Promise<ConstructionSiteName[]> => {
    const query = queryString.stringify({searchTerm, searchLimit, contractId});
    return ApiProvider.getAsync<ConstructionSiteName[]>(`/api/ConstructionSites/SearchConstructionSiteNames?${query}`, null);
};

export const getServiceRequestDefinitionId = async (serviceType: ServiceType): Promise<string> => {
    return ApiProvider.postAsync<string>(`/api/ConstructionSites/GetServiceDefinitionId`, serviceType, null);
};

export const getConstructionSiteEquipment = async (contractId: string | null): Promise<IEquipmentPageData> => {
    return ApiProvider.postAsync<IEquipmentPageData>("/api/ConstructionSites/Equipment", contractId, null);
};

export const getProductsWithTelematics = async (
    constructionSiteId: string | null,
    pageNumber: number,
    pageSize: number,
    organizationId: string | null,
    sortColumnName: string | null,
    sortDirection: SortDirection | null,
    userContractId: string | null,
    userSiteId: string | null,
    deviceNames: string[],
): Promise<IPagedList<ProductWithTelematics>> => {
    const request : ProductsWithTelematicsRequest = {
        constructionSiteId,
        pageNumber,
        pageSize,
        organizationId: organizationId,
        sortColumnName: sortColumnName,
        sortDirection: sortDirection,
        userRoleContractId: userContractId,
        userRoleConstructionSiteId: userSiteId,
        deviceNames,
    };
    return ApiProvider.postAsync<IPagedList<ProductWithTelematics>>("/api/ConstructionSites/ProductsWithTelematics", request, null);
};

export const getProductsWithTelematicsDetails = async (
    internalId: string,
    rentaId: string | null,
    constructionSiteId: string | null,
): Promise<ProductWithTelematicsDetails> => {
    const request: ProductWithTelematicsDetailsRequest = {
        internalId: internalId,
        rentaId: rentaId,
        constructionSiteId,
    };

    return ApiProvider.postAsync("/api/ConstructionSites/ProductWithTelematicsDetails", request);
};

export const getProductEquipment = async (request: IEquipmentRequest): Promise<IEquipmentPageData> => {
    return ApiProvider.postAsync<IEquipmentPageData>("/api/Product/Equipment", request, null);
};

export const createConstructionSiteServiceRequest = async (request: CreateConstructionSiteServiceRequestRequest): Promise<void> => {
    return ApiProvider.postAsync<void>("/api/Services/CreateConstructionSiteServiceRequest", request, null);
};

export const logDocumentEvent = async (request: LogDocumentEvent): Promise <void> => {
    return ApiProvider.postAsync<void>("/api/ConstructionSites/LogDocumentEvent", request, null);
}

export const postReturnProduct = async (returnModel: ReturnModel): Promise<void> => {
    return ApiProvider.postAsync<void>("/api/ConstructionSites/ReturnProduct", returnModel, null);
};

export const postInviteUser = async (inviteUserRequest: InviteUserPostRequest): Promise<IResponse> => {
    return ApiProvider.postAsync<IResponse>("/api/Users/InviteUser", inviteUserRequest, null);
};

export const resolveSiteLocation = async (resolveSiteLocationRequest: ResolveLocationRequest): Promise<LocationModel | null> => {
    return ApiProvider.postAsync<LocationModel>("/api/Locations/ResolveSiteLocation", resolveSiteLocationRequest, null);
};

export const getConstructionSitesRentalCosts = async (id: string): Promise<BasicChartModel[]> => {
    const query = queryString.stringify({id});
    return ApiProvider.getAsync<BasicChartModel[]>(`/api/ConstructionSites/GetStatusRentalCosts?${query}`, null);
};

export const getEquipmentEmissionChartData = async (id: string): Promise<BasicChartModel[]> => {
    const request: GetEquipmentEmissionData = {
        constructionSiteId: id,
        contractId: null,
        emissionType: null,
        dateRangeFrom: null,
        dateRangeTo: null,
        emissionFilterTimeFrame: EmissionFilterTimeFrame.Monthly,
    }
    return ApiProvider.postAsync<BasicChartModel[]>(`/api/Emissions/GetEquipmentEmissionChartData`, request);
};

export const getConstructionSitesPowerConsumptionByExternalId = async (id: string): Promise<BasicChartModel[]> => {
    const query = queryString.stringify({id});
    return ApiProvider.getAsync<BasicChartModel[]>(`/api/ConstructionSites/GetConstructionSitesPowerConsumptionByExternalId?${query}`, null);
};

export const getConstructionSitesPowerInfoByExternalId = async (id: string): Promise<SitePowerInfoModel | null> => {
    const query = queryString.stringify({id});
    return ApiProvider.getAsync(`/api/ConstructionSites/GetConstructionSitesPowerInfo?${query}`, null);
};

export const getPowerSupplyData = async (id: string, powerSupplyId: number): Promise<BasicChartModel[]> => {

    const query = queryString.stringify({powerSupplyId, id});
    return ApiProvider.getAsync(`/api/ConstructionSites/GetPowerSupplyDailyMaxCurrent?${query}`, null);
};

export const getSiteUsers = async (siteId: string, includeOrganizationUsers: boolean = false): Promise<ConstructionSiteUserModel[]> => {
    const request: ConstructionSiteUserPagedListRequest = {
        constructionSiteId: siteId,
        pageSize: 1000,
        pageNumber: 1,
        searchTerm: null,
        sortDirection: null,
        selectedRole: null,
        sortColumnName: null,
        notProcessedUsers: false,
        includeOrganizationUsers: includeOrganizationUsers,
    };

    const users: IPagedList<ConstructionSiteUserModel> = await ApiProvider.postAsync("/api/Users/GetConstructionSiteUsersPaged", request);

    request.notProcessedUsers = true;

    const invitedUsers:  IPagedList<ConstructionSiteUserModel> = await ApiProvider.postAsync("/api/Users/GetConstructionSiteUsersPaged", request);

    return [...users.items,...invitedUsers.items];
};

export const saveSiteLocation = async (siteId: string, location: GeoLocation | null): Promise<void> => {
    const request: SaveSiteLocationRequest = {
        location: location,
        constructionSiteId: siteId,
    };
    return ApiProvider.postAsync<void>("/api/ConstructionSites/SaveSiteLocation", request);
}

export const downloadCsvReport = async (products: ProductWithTelematicsDetailsRequest[], constructionSiteName: string, constructionSiteOwner: string, constructionSiteId: string): Promise<void> => {

    const request: EquipmentDataReportRequest = {
        products: products,
        constructionSiteName: constructionSiteName,
        constructionSiteOwner: constructionSiteOwner,
        constructionSiteId: constructionSiteId,
    };

    const response: FileModel = await ApiProvider.postAsync("/api/ConstructionSites/DownloadCsvReport", request);

    ch.download(response);
}

export const getConstructionSiteActivelyRentedProductsCount = async (customerId: string | null, siteId: string, siteExternalId: string | null): Promise<number> => {
    const query = queryString.stringify({customerId, siteId, siteExternalId});
    return ApiProvider.getAsync<number>(`/api/ConstructionSites/GetConstructionSiteActivelyRentedProductsCount?${query}`, null);
}